import { Locale } from '@headless-workspace/config';
import {
    Repository,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import {
    AuthorizationStrategy,
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
    QueryParamStrategy,
    RefreshTokenStrategy,
} from '@headless-workspace/data';
import { DecoderHelpers } from '@headless-workspace/utils';
import { VipBrandsValue } from '../config';
import { ProductRecommendationContainerDTO, ProductRecommendationDTO } from './ProductRecommendationDTO';
import { mapProductRecommendation, ProductRecommendationValue } from './ProductRecommendationValue';

export interface ProductRecommendationsRepositorySpec {
    getProductRecommendation(
        locale: Locale,
        productId: string,
        pageId: string,
        vipBrands: VipBrandsValue,
        originalPriceText?: string,
    ): Promise<Result<ProductRecommendationValue[]>>;
}

export class ProductRecommendationsRepository extends Repository implements ProductRecommendationsRepositorySpec {
    readonly path = '/catalog/v1';

    constructor(
        serverAuthStrategy: Partial<AuthorizationStrategy> = memoryCacheAuthStrategy,
        serverRefreshTokenStrategy: Partial<RefreshTokenStrategy> = memoryCacheRefreshTokenStrategy,
        serverQueryParamStrategy: Partial<QueryParamStrategy> = memoryCacheQueryParamStrategy,
    ) {
        super(serverAuthStrategy, serverRefreshTokenStrategy, serverQueryParamStrategy);
    }

    async getProductRecommendation(
        locale: Locale,
        productId: string,
        pageId: string,
        vipBrands: VipBrandsValue,
        originalPriceText?: string,
    ): Promise<Result<ProductRecommendationValue[]>> {
        try {
            const recommendationsResponse = await this.datasource.getResource(`${this.path}/recommendations`, {
                productId,
                locale,
                pageId,
            });

            const productRecommendationArray = ProductRecommendationContainerDTO.parse(recommendationsResponse);

            const productRecommendation = DecoderHelpers.safeParseArray(
                productRecommendationArray.recommendations,
                ProductRecommendationDTO,
            );

            if (productRecommendation.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(mapProductRecommendation(productRecommendation, vipBrands, locale, originalPriceText));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }
}
